import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  DetailsList,
  IColumn,
  MoreLessToggle,
  RESET_CELLS_PADDING_CLASSNAME,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { CalendarEventType } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { ClaimDocumentViewerDialog } from "@modules/acc/screens/claims/components/ClaimDocumentViewerDialog.tsx";
import {
  ClaimNumberCell,
  EM_DASH_SYMBOL
} from "@modules/acc/screens/claims/components/ClaimNumberCell.tsx";
import { ClaimsContextualMenu } from "@modules/acc/screens/claims/components/ClaimsContextualMenu.tsx";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { UserFetcher } from "@ui-components/UserFetcher.tsx";

import { ConditionsContextualMenu } from "./ConditionsContextualMenu.tsx";
import { renderDiagnosisCell, sortEpisodesOfCare } from "./utils.tsx";

interface ConditionListProps {
  episodesOfCare: EpisodeOfCare[];
  hideClaimNumberColumn?: boolean;
}

export const ConditionsList: React.FC<ConditionListProps> = observer(
  ({ episodesOfCare, hideClaimNumberColumn }) => {
    const longDashSymbol = String.fromCodePoint(EM_DASH_SYMBOL);
    const sortedEpisodesOfCare = sortEpisodesOfCare(episodesOfCare);
    const { booking } = useStores();

    const [results, setResults] = useState<EpisodeOfCare[]>(
      sortedEpisodesOfCare.slice(0, 3)
    );

    const theme = useTheme();

    const showMore = results.length < sortedEpisodesOfCare.length;

    const moreStateText = `Show more (${
      sortedEpisodesOfCare.length - results.length < 3
        ? sortedEpisodesOfCare.length - results.length
        : 3
    })`;

    const getMore = async () => {
      setResults(prev => [
        ...prev,
        ...sortedEpisodesOfCare.slice(prev.length, prev.length + 3)
      ]);
    };

    return (
      <Stack tokens={{ childrenGap: 8 }}>
        <DetailsList
          items={results}
          columns={[
            {
              name: "",
              className: RESET_CELLS_PADDING_CLASSNAME,
              key: "action",
              minWidth: 20,
              maxWidth: 20,
              onRender: (item: EpisodeOfCare) =>
                item.claim ? (
                  <ClaimsContextualMenu
                    claim={item.claim}
                    displayConditionSetup
                    hideChangeToPrivate
                    newApptMenuItem={{
                      key: "newLinked",
                      text: "New linked appointment",
                      onClick: () => {
                        booking.ui.showCalendarEventDialog({
                          type: CalendarEventType.Appointment,
                          initialValues: {
                            patientId: item.patientId,
                            episodeOfCareId: item.id
                          }
                        });
                      }
                    }}
                  />
                ) : (
                  <ConditionsContextualMenu episodeOfCare={item} />
                )
            },
            {
              name: "Claim",
              key: "claimNumber",
              minWidth: 80,
              maxWidth: 80,
              onRender: (item: EpisodeOfCare) =>
                item.claim ? (
                  <ClaimNumberCell hideReferalToolTip claim={item.claim!} />
                ) : (
                  longDashSymbol
                )
            },
            {
              name: "Diagnosis",
              key: "injuryDiagnosis",
              minWidth: 120,
              maxWidth: 120,
              onRender: (item: EpisodeOfCare) =>
                renderDiagnosisCell(item, theme)
            },
            {
              name: "Next appt",
              key: "appt",
              minWidth: 80,
              maxWidth: 80,
              onRender: (item: EpisodeOfCare) => {
                if (item.nextCalendarEventDate) {
                  return item.nextCalendarEventDate.toDayDefaultFormat();
                } else {
                  return longDashSymbol;
                }
              }
            },
            {
              name: "Provider",
              key: "provider",
              minWidth: 120,
              maxWidth: 120,
              onRender: (item: EpisodeOfCare) =>
                item.nextCalendarEventProviderId ? (
                  <UserFetcher userId={item.nextCalendarEventProviderId}>
                    {provider => <Text>{provider.fullName}</Text>}
                  </UserFetcher>
                ) : (
                  <Text>{longDashSymbol}</Text>
                )
            }
          ].filter(
            (col: IColumn) =>
              !(col.key === "claimNumber" && hideClaimNumberColumn)
          )}
        />
        {showMore && (
          <MoreLessToggle
            linkProps={{ moreStateText }}
            onChanged={getMore}
            value={true}
          />
        )}
        <ClaimDocumentViewerDialog />
      </Stack>
    );
  }
);
